<template>
  <LoadingSpinner v-if="loading" />
  <v-row v-else>
    <v-col cols="12">
      <v-card>
        <v-row>
          <v-col>
            <v-card-title>{{ product.name }}</v-card-title>
            <v-card-subtitle>
              <div>
                <span class="font-weight-medium">Core Products:</span>
                {{ (product.coreProducts || []).map((x) => x.name).join(', ') }}
                <a
                  class="mx-2 info--text subtitle-2"
                  @click="$refs.editCoreProducts.show(product)"
                >
                  edit
                </a>
                <EditCoreProducts ref="editCoreProducts" />
              </div>
              <div>
                <span class="font-weight-medium">Provider:</span>
                {{ print_provider_name(product.providerId) }}
              </div>
              <div>
                <span class="font-weight-medium">Size:</span>
                {{ product.height }}" x {{ product.width }}"
              </div>
              <div>
                <span class="font-weight-medium">External Id:</span>
                {{ product.externalId }}
              </div>
              <div>
                <span class="font-weight-medium">Material:</span>
                {{ product.material.display }}
              </div>
            </v-card-subtitle>
          </v-col>
          <v-spacer />
          <v-col
            cols="auto"
            class="my-auto"
          >
            <v-btn
              :loading="refreshing"
              @click="refresh"
            >
              <v-icon>refresh</v-icon> Sync
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
            class="my-auto mr-4"
          >
            <v-btn to="/print"> Return to Products </v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
    <v-col cols="12">
      <v-card :loading="saving">
        <v-card-title>
          Quantities
          <v-spacer />
          <v-select
            v-model="masterAccountId"
            label="Master Account"
            :items="masterAccounts"
            item-value="id"
            item-text="name"
            outlined
            hide-details
            dense
            class="min-width"
          />
          <v-btn
            v-if="product.providerId != $enums.PROVIDER.FOUROVER"
            class="ml-4"
            @click="
              $bus.$emit($enums.EVENT_BUS_EVENTS.EDIT_PRINT_QUANTITY_SHOW, {
                product
              })
            "
          >
            Add
          </v-btn>
          <EditPrintQuantity @save="loadData" />
        </v-card-title>
        <v-divider />
        <v-data-table
          :loading="loading"
          :headers="quantity_headers"
          :items="quantitiesToDisplay"
          disable-pagination
          hide-default-footer
          group-by="colorSpec.name"
          :sort-by="['value']"
          mobile-breakpoint="0"
        >
          <template #[`group.header`]="{ items, isOpen, toggle }">
            <th colspan="100%">
              <v-icon @click="toggle">
                {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
              </v-icon>
              {{ items[0].colorSpec.name }}
            </th>
          </template>
          <template #[`item.value`]="{ item }">
            {{ item.value | separator }}
          </template>
          <template #[`item.cost`]="{ item }">
            {{ item.cost | currency }}
          </template>
          <template #[`item.turnarounds`]="{ item }">
            <div
              v-for="(turnaround, i) in turnarounds(item)"
              :key="i"
            >
              {{ turnaround.turnaroundType.display }}:
              <template v-if="turnaround.cost">
                {{ turnaround.cost | currency }}
              </template>
              <template v-else-if="turnaround.costPerUnit">
                {{ turnaround.costPerUnit | currency }}/unit
              </template>
              <template v-else> No Cost </template>
              <template v-if="turnaround.shippingCost">
                ({{ turnaround.shippingCost | currency }} shipping)
              </template>
            </div>
          </template>
          <template #[`item.actions`]="{ item }">
            <template v-if="product.providerId != $enums.PROVIDER.FOUROVER">
              <v-btn
                class="mr-2"
                @click="
                  $bus.$emit($enums.EVENT_BUS_EVENTS.EDIT_PRINT_QUANTITY_SHOW, {
                    quantity: item,
                    product
                  })
                "
              >
                Edit
              </v-btn>
              <v-btn
                color="error"
                @click="deleteQuantity(item.id)"
              >
                Delete
              </v-btn>
            </template>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
    <v-col cols="12">
      <v-card :loading="saving">
        <v-card-title> Options </v-card-title>
        <v-divider />
        <v-data-table
          :loading="loading"
          :headers="option_headers"
          :items="optionsToDisplay"
          disable-pagination
          hide-default-footer
          mobile-breakpoint="0"
          group-by="option.group.name"
          :sort-by="['option.name', 'startQuantity']"
        >
          <template #[`group.header`]="{ items, isOpen, toggle }">
            <th colspan="100%">
              <v-icon @click="toggle">
                {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
              </v-icon>
              {{ items[0].option.group.name }}
            </th>
          </template>
          <template #[`item.startQuantity`]="{ item }">
            {{ item.startQuantity | separator }}
          </template>
          <template #[`item.endQuantity`]="{ item }">
            {{ item.endQuantity | separator }}
          </template>
          <template #[`item.cost`]="{ item }">
            {{ item.cost | currency }}
          </template>
          <template #[`item.costPerUnit`]="{ item }">
            {{ item.costPerUnit | currencySmall }}
          </template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import EditCoreProducts from '@/components/print/EditCoreProducts';
import EditPrintQuantity from '@/components/print/EditQuantity';
import { orderBy, uniq } from 'lodash';
import { mapGetters } from 'vuex';
export default {
  components: { EditCoreProducts, EditPrintQuantity },
  data: () => ({
    loading: true,
    refreshing: false,
    saving: false,
    quantity: 0,
    quantity_headers: [
      { text: 'Color Spec', value: 'colorSpec.name' },
      { text: 'Qty', value: 'value' },
      { text: 'Cost', value: 'cost' },
      { text: 'External Id', value: 'externalId' },
      { text: 'Group Ship?', value: 'canGroupShip' },
      { text: 'Turnaround', value: 'turnarounds' },
      { text: '', value: 'actions' }
    ],
    option_headers: [
      { text: 'Type', value: 'option.group.name' },
      { text: 'Name', value: 'option.name' },
      { text: 'Desc', value: 'option.description' },
      { text: 'Start Qty', value: 'startQuantity' },
      { text: 'End Qty', value: 'endQuantity' },
      { text: 'Cost', value: 'cost' },
      { text: 'Cost/Unit', value: 'costPerUnit' },
      { text: 'Cost Qty', value: 'costQuantity' },
      { text: 'External Id', value: 'option.externalId' },
      { text: 'Price External Id', value: 'externalId' },
      { text: '', value: 'actions' }
    ],
    masterAccountId: null
  }),
  computed: {
    id() {
      return this.$route.params.id;
    },
    optionsToDisplay() {
      return this.product.options.filter((x) => x.masterAccountId === this.masterAccountId);
    },
    quantitiesToDisplay() {
      return this.product.quantities.filter((x) => x.masterAccountId === this.masterAccountId);
    },
    turnarounds() {
      return (item) => {
        return orderBy(item.turnarounds, ['turnaroundType.days'], ['desc']);
      };
    },
    masterAccounts() {
      const ids = uniq(this.product.quantities.map((x) => x.masterAccountId));
      return ids.map((x) => ({
        id: x,
        name: this.master_account_name(x) || '*** Default ***'
      }));
    },
    ...mapGetters({
      list: 'print/list',
      product: 'print/product',
      print_provider_name: 'lookup/print_provider_name',
      master_account_name: 'lookup/master_account_name'
    })
  },
  created() {
    this.loadData(true);
  },
  methods: {
    loadData(initial) {
      this.loading = true;
      const calls = [this.$store.dispatch('print/getProduct', this.id)];
      if (initial) {
        calls.push(this.$store.dispatch('lookup/getProducts'));
        calls.push(this.$store.dispatch('print/getLookups'));
      }
      Promise.all(calls)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refresh() {
      this.refreshing = true;
      this.$store
        .dispatch('print/refreshProduct', this.id)
        .catch((error) => {
          this.logError(error);
        })
        .finally(() => {
          this.refreshing = false;
        });
    },
    async deleteQuantity(id) {
      if (
        await this.$root.$confirm(
          'Delete Print Quantity',
          'Are you sure you want to delete this print quantity?',
          { color: 'error' }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch('print/deleteQuantity', id)
          .then(() => {
            this.drawer = false;
            this.$root.$snackbarSuccess('Print Quantity deleted');
          })
          .catch((error) => {
            this.logError(error);
          })
          .finally(() => {
            this.saving = false;
          });
      }
    }
  }
};
</script>
